import first from "lodash/first";
import last from "lodash/last";
import { type FunctionComponent, useCallback, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { findFirstNonEmptyString } from "scmp-sdk";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import { Share } from "scmp-app/components/article/article-2.0-action-widget/share";
import { useArticleListContext } from "scmp-app/components/article/article-list/contexts";
import type { ArticleRenderChildProps } from "scmp-app/components/article/article-render";
import { useArticleBaseAdvertisingInfo } from "scmp-app/components/article/article-render/hooks";
import { VideoPlayMode } from "scmp-app/components/scmp-youtube-video/enums";
import type { videoArticleContent$key } from "scmp-app/queries/__generated__/videoArticleContent.graphql";

import {
  ActionWidgets,
  Author,
  AuthorDetail,
  Container,
  Content,
  DesktopHeaderAdSlot,
  FooterAd,
  Headline,
  PlayerContainer,
  PublishDateTime,
  RelatedTopic,
  RightSectionContainer,
  StyledArticleGoogleNewsButton,
  StyledSCMPYoutubeVideo,
  StyledUpNextWidget,
  SubHeadline,
  TopicAndSection,
  UpNextWidgetContainer,
} from "./styles";

export type Props = ArticleRenderChildProps & {
  className?: string;
  reference: videoArticleContent$key;
};

export const VideoArticle: FunctionComponent<Props> = ({ provider, reference }) => {
  const video = useFragment<videoArticleContent$key>(
    graphql`
      fragment videoArticleContent on Video {
        duration
        entityId
        headline
        socialHeadline
        orientation
        youtubeId
        videoSource {
          id
          type
        }
        sectionsV2 {
          value {
            ... on VideoSection {
              entityId
            }
          }
        }
        image(filter: { type: MAIN_IMAGE }) {
          landscape: style(filter: { style: "wide_landscape" }) {
            url
          }
          portrait: style(filter: { style: "portrait" }) {
            url
          }
        }
        ...articleBaseAdvertisingInfoArticle
        ...shareArticle20Content
      }
    `,
    reference,
  );

  useArticleBaseAdvertisingInfo(video);

  const { firstArticleEntityId } = useArticleListContext();

  const isPortrait = video.orientation === "portrait";
  const isFirstPosition = video.entityId === firstArticleEntityId;
  const imageUrl = isPortrait ? video.image?.portrait?.url : video.image?.landscape?.url;
  const mainSectionId = last(first(video.sectionsV2)?.value)?.entityId;

  const [autoplayEnabled, setAutoplayEnabled] = useState(true);
  const [videoIdUrlPair, setVideoIdUrlPair] = useState<string[][]>([]);

  const title = useMemo(
    () => findFirstNonEmptyString(video?.socialHeadline, video?.headline),
    [video?.headline, video?.socialHeadline],
  );

  const youtubeId = useMemo(
    () => video.youtubeId ?? video.videoSource?.find(source => source?.type === "youtube")?.id,
    [video.videoSource, video.youtubeId],
  );

  const handleVideoEnded = useCallback(() => {
    if (!autoplayEnabled) return;

    const currentIndex = videoIdUrlPair.findIndex(([entityId]) => entityId === video.entityId);
    if (currentIndex === -1) return;

    const [, url] = videoIdUrlPair[currentIndex + 1] ?? [];
    if (!notEmpty(url)) return;

    window.location.href = url;
  }, [autoplayEnabled, video.entityId, videoIdUrlPair]);

  const handleSelectVideo = useCallback(
    (id: string) => {
      const [, url] = videoIdUrlPair.find(([entityId]) => entityId === id) ?? [];
      if (!notEmpty(url)) return;

      window.location.href = url;
    },
    [videoIdUrlPair],
  );

  const handleToggleAutoplay = useCallback((enabled: boolean) => {
    setAutoplayEnabled(enabled);
  }, []);

  const handleVideoSync = useCallback((videos: string[][]) => {
    setVideoIdUrlPair(videos);
  }, []);

  return (
    <Container $isPortrait={isPortrait}>
      <DesktopHeaderAdSlot withSticky={false}>
        {provider.adslot({
          adUnit: "d_banner1",
          breakpoint: theme.breakpoints.up("desktop"),
          sizes: [
            [970, 250],
            [728, 90],
            [970, 90],
          ],
        })}
      </DesktopHeaderAdSlot>

      <PlayerContainer $isFirstArticle={isFirstPosition} $isPortrait={isPortrait}>
        {youtubeId && (
          <StyledSCMPYoutubeVideo
            duration={video.duration ?? undefined}
            enableAutoPlay={isFirstPosition}
            entityId={video.entityId}
            gaData={{
              ga4ArticleEntityId: video.entityId,
              ga4Position: "video_article",
            }}
            imageSource={imageUrl ?? undefined}
            index={0}
            initialVideoPlayMode={isFirstPosition ? VideoPlayMode.Autoplay : VideoPlayMode.Unset}
            isEnabledImaManager
            isMuteVideo={false}
            onEnded={handleVideoEnded}
            orientation={isPortrait ? "portrait" : "landscape"}
            title={title}
            youtubeVideoId={youtubeId}
          />
        )}
      </PlayerContainer>

      <TopicAndSection>
        <BaseLinkContextProvider
          customQueryParameters={{
            module: "breadcrumb",
            pgtype: "article",
          }}
        >
          {provider.topic()}
          {provider.sections({ variant: "video" })}
        </BaseLinkContextProvider>
      </TopicAndSection>

      <Headline>{provider.headline({ hideTag: true })}</Headline>

      <SubHeadline>{provider.subHeadline()}</SubHeadline>

      <ActionWidgets>
        <Share position="article_header" reference={video} />
        {provider.actionWidget({ variant: "video" })}
        <StyledArticleGoogleNewsButton articleId={video.entityId} />
      </ActionWidgets>

      <Author>{provider.authorNames()}</Author>

      <PublishDateTime>{provider.publishedDate({ variant: "article-2.0" })}</PublishDateTime>

      <Content>
        {provider.body({
          isDisabledArticleImageGallery: true,
        })}
      </Content>

      <AuthorDetail>
        {provider.authorDetails({
          variant: "generic",
        })}
      </AuthorDetail>

      <RelatedTopic>{provider.relatedTopics()}</RelatedTopic>

      <FooterAd>
        {provider.adslot({
          adUnit: "m_banner3",
          breakpoint: theme.breakpoints.down("desktop"),
          sizes: [
            [320, 100],
            [320, 50],
            [300, 100],
            [300, 50],
          ],
          withLabel: true,
        })}
      </FooterAd>

      <RightSectionContainer>
        {provider.adslot({
          adUnit: "d_lrec1",
          breakpoint: theme.breakpoints.up("desktop"),
          sizes: [
            [300, 250],
            [300, 600],
          ],
          withSticky: true,
        })}
      </RightSectionContainer>

      {notEmpty(mainSectionId) && (
        <ClientSideSuspense>
          <UpNextWidgetContainer>
            <StyledUpNextWidget
              autoplayEnabled={autoplayEnabled}
              currentVideoId={video.entityId}
              onSelect={handleSelectVideo}
              onSyncVideos={handleVideoSync}
              onToggleAutoplay={handleToggleAutoplay}
              sectionId={mainSectionId}
            />
          </UpNextWidgetContainer>
        </ClientSideSuspense>
      )}
    </Container>
  );
};

VideoArticle.displayName = "VideoArticle";
